// ############################################################
// Copyright (C) 2021-2025 by Gamify Software Inc - All Rights Reserved
// You may NOT distribute or modify this code without the expressed
// written consent of Gamify Software Inc.
// #############################################################

import React, { useMemo, useState, useEffect } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { Home as HomeIcon } from '@mui/icons-material';
import { API, graphqlOperation } from 'aws-amplify';
import { useTheme } from '../../../context/PermissionsContext/ThemeContext/ThemeContext';
import featureFlag from '../../../common/featureFlag';

// Only request fields that exist in the web AppSync schema
const getAddressForDoorData = /* GraphQL */ `
  query GetAddress($id: ID!) {
    getAddress(id: $id) {
      id
      street
      address
      city
      region
      postalCode
      distanceFromDoor
      creditEstimate
      age
      income
      lengthOfResidence
      flexAttributes
    }
  }
`;

interface DealDoorDataProps {
  dealInfo: any;
}

function numberWithCommas(x: any): string {
  if (x === null || x === undefined || x === '') {
    return '';
  }
  return Number(x).toLocaleString();
}

function wholeDollarWithCommas(x: any): string {
  if (x === null || x === undefined || x === '') {
    return '';
  }
  const num = Number(x);
  if (isNaN(num)) {
    return String(x);
  }
  return '$' + Math.round(num).toLocaleString();
}

function decodeEstimatedCreditRating(ratingCode: string | null): string {
  if (!ratingCode) {
    return '?';
  }
  const ratings: Record<string, string> = {
    A: '800+',
    B: '750-799',
    C: '700-749',
    D: '650-699',
    E: '600-649',
    F: '550-599',
    G: '500-549',
    H: '< 499',
  };
  return ratings[ratingCode] || '?';
}

const metersToFeet = (meters: number): number => Math.round(meters * 3.28084);

const DealDoorData: React.FC<DealDoorDataProps> = ({ dealInfo }) => {
  const { theme } = useTheme();
  const [addressData, setAddressData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);

  const useDoorData = (global as any)?.appSettings?.features?.route
    ?.useDoorData;
  const noDoorData = (global as any)?.appSettings?.features?.route?.noDoorData;
  const enhancedDoorData = (global as any)?.appSettings?.features?.route
    ?.enhancedDoorData;
  const showDistanceFromDoor = (global as any)?.appSettings?.features?.route
    ?.showDistanceFromDoor;

  // Fetch address data using the deal ID
  useEffect(() => {
    async function fetchAddress() {
      if (!dealInfo?.id) {
        return;
      }
      setIsLoading(true);
      try {
        const result: any = await API.graphql(
          graphqlOperation(getAddressForDoorData, { id: dealInfo.id })
        );
        if (result?.data?.getAddress) {
          setAddressData(result.data.getAddress);
        }
      } catch (err) {
        console.error('Error fetching address for door data:', err);
      } finally {
        setIsLoading(false);
      }
    }
    fetchAddress();
  }, [dealInfo?.id]);

  // Parse deal-level flexAttributes for fallback address info
  const dealFlex = useMemo(() => {
    if (!dealInfo?.flexAttributes) {
      return {};
    }
    try {
      return typeof dealInfo.flexAttributes === 'string'
        ? JSON.parse(dealInfo.flexAttributes)
        : dealInfo.flexAttributes;
    } catch {
      return {};
    }
  }, [dealInfo?.flexAttributes]);

  // Parse address-level flexAttributes for household fields
  const addrFlex = useMemo(() => {
    if (!addressData?.flexAttributes) {
      return {};
    }
    try {
      return typeof addressData.flexAttributes === 'string'
        ? JSON.parse(addressData.flexAttributes)
        : addressData.flexAttributes;
    } catch {
      return {};
    }
  }, [addressData?.flexAttributes]);

  const addr = addressData || {};

  // Build full address string (must be before early returns to satisfy hook rules)
  const fullAddress = useMemo(() => {
    const parts = [
      dealFlex.Address || dealFlex.address || addr.address || addr.street,
      dealFlex.City || dealFlex.city || addr.city,
      dealFlex.State || dealFlex.state || addr.region,
      dealFlex.ZipCode || dealFlex.zipCode || dealFlex.zip || addr.postalCode,
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : null;
  }, [dealFlex, addr]);

  // If org has door data disabled, show nothing
  if (noDoorData || !useDoorData) {
    return null;
  }

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          py: 5,
        }}
      >
        <CircularProgress size={28} sx={{ color: theme.primary }} />
      </Box>
    );
  }

  // Helper: pick first truthy value from address top-level, address flex, deal flex
  const pick = (...keys: string[]) => {
    for (const k of keys) {
      if (addr[k]) {
        return addr[k];
      }
      if (addrFlex[k]) {
        return addrFlex[k];
      }
      if (dealFlex[k]) {
        return dealFlex[k];
      }
    }
    return null;
  };

  // Two-column household data rows matching mobile HouseholdInformation layout
  const rows: {
    left: { label: string; value: string };
    right: { label: string; value: string };
  }[] = [];

  rows.push({
    left: {
      label: 'Est. Credit Rating',
      value: enhancedDoorData
        ? decodeEstimatedCreditRating(
            pick('estimatedAreaCreditRating', 'creditEstimate')
          )
        : '',
    },
    right: {
      label: 'Income',
      value: enhancedDoorData
        ? pick('income')
          ? wholeDollarWithCommas(pick('income'))
          : ''
        : '',
    },
  });

  rows.push({
    left: {
      label: 'Home Value',
      value: enhancedDoorData
        ? pick('medianHomeValue')
          ? wholeDollarWithCommas(pick('medianHomeValue'))
          : ''
        : '',
    },
    right: {
      label: 'SqFt',
      value: enhancedDoorData
        ? pick('homeSquareFootage')
          ? numberWithCommas(pick('homeSquareFootage'))
          : ''
        : '',
    },
  });

  rows.push({
    left: {
      label: 'Year built',
      value: enhancedDoorData ? pick('homeBuiltYear') || '' : '',
    },
    right: {
      label: 'Last sold',
      value: enhancedDoorData
        ? pick('lengthOfResidence')
          ? String(new Date().getFullYear() - Number(pick('lengthOfResidence')))
          : ''
        : '',
    },
  });

  rows.push({
    left: {
      label: 'Residence Length',
      value: enhancedDoorData
        ? pick('lengthOfResidence')
          ? Number(pick('lengthOfResidence')) > 1
            ? pick('lengthOfResidence') + ' years'
            : '1 year or less'
          : ''
        : '',
    },
    right: {
      label: 'Owner age',
      value: enhancedDoorData ? pick('age') || '' : '',
    },
  });

  // Weather events from address flexAttributes
  let weatherInfo: any[] = [];
  if (featureFlag('weatherOverlay')) {
    if (Array.isArray(addrFlex?.weather_events)) {
      weatherInfo = addrFlex.weather_events;
    }
  }

  const hasAnyData = rows.some(
    (r) => r.left.value !== '' || r.right.value !== ''
  );

  return (
    <Box sx={{ p: 2 }}>
      <Typography
        sx={{ fontSize: 18, fontWeight: 600, color: theme.text, mb: 2 }}
      >
        Door Data
      </Typography>

      {fullAddress && (
        <Box
          sx={{
            backgroundColor: theme.background2,
            borderRadius: 2,
            p: 2,
            mb: 2,
          }}
        >
          <Typography sx={{ fontSize: 12, color: theme.textLight, mb: 0.5 }}>
            Property Address
          </Typography>
          <Typography sx={{ fontSize: 16, color: theme.text, fontWeight: 500 }}>
            {fullAddress}
          </Typography>
        </Box>
      )}

      {/* Weather Events Table */}
      {weatherInfo.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography
            sx={{ fontSize: 14, fontWeight: 700, color: theme.text, mb: 1 }}
          >
            Weather Events Detected
          </Typography>
          <Box
            sx={{
              borderRadius: 1.5,
              overflow: 'hidden',
              border: `1px solid ${theme.stroke}`,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                backgroundColor: theme.background2,
                px: 1.5,
                py: 1,
                borderBottom: `1px solid ${theme.stroke}`,
              }}
            >
              <Typography
                sx={{
                  flex: 1,
                  fontSize: 12,
                  fontWeight: 600,
                  color: theme.text,
                }}
              >
                Date
              </Typography>
              <Typography
                sx={{
                  flex: 1,
                  fontSize: 12,
                  fontWeight: 600,
                  color: theme.text,
                }}
              >
                Event
              </Typography>
              <Typography
                sx={{
                  flex: 1,
                  fontSize: 12,
                  fontWeight: 600,
                  color: theme.text,
                }}
              >
                Hail Size
              </Typography>
            </Box>
            {weatherInfo.map((event: any, i: number) => (
              <Box
                key={i}
                sx={{
                  display: 'flex',
                  px: 1.5,
                  py: 1,
                  backgroundColor:
                    i % 2 === 0 ? theme.background2 : theme.background1,
                  borderBottom:
                    i < weatherInfo.length - 1
                      ? `1px solid ${theme.stroke}`
                      : 'none',
                }}
              >
                <Typography sx={{ flex: 1, fontSize: 12, color: theme.text }}>
                  {event.event_date || 'N/A'}
                </Typography>
                <Typography sx={{ flex: 1, fontSize: 12, color: theme.text }}>
                  {event.event_type || 'N/A'}
                </Typography>
                <Typography sx={{ flex: 1, fontSize: 12, color: theme.text }}>
                  {event.hail_size ? `${event.hail_size}"` : 'N/A'}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Household data - two column layout matching mobile */}
      {hasAnyData ? (
        <Box
          sx={{
            backgroundColor: theme.background2,
            borderRadius: 2,
            p: 2,
          }}
        >
          {rows.map((row, i) => (
            <Box
              key={i}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                mb: i < rows.length - 1 ? 0.75 : 0,
              }}
            >
              <Typography sx={{ fontSize: 14, color: theme.text }}>
                <Box component="span" sx={{ fontWeight: 500 }}>
                  {row.left.label}:{' '}
                </Box>
                <Box component="span" sx={{ fontWeight: 300 }}>
                  {row.left.value}
                </Box>
              </Typography>
              <Typography sx={{ fontSize: 14, color: theme.text }}>
                <Box component="span" sx={{ fontWeight: 500 }}>
                  {row.right.label}:{' '}
                </Box>
                <Box component="span" sx={{ fontWeight: 300 }}>
                  {row.right.value}
                </Box>
              </Typography>
            </Box>
          ))}

          {/* Distance from door */}
          {showDistanceFromDoor && addr.distanceFromDoor != null && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                mt: 0.75,
              }}
            >
              <Typography sx={{ fontSize: 14, color: theme.text }}>
                <Box component="span" sx={{ fontWeight: 500 }}>
                  Distance from door:{' '}
                </Box>
                <Box component="span" sx={{ fontWeight: 300 }}>
                  {addr.distanceFromDoor !== ''
                    ? `${metersToFeet(Number(addr.distanceFromDoor))} ft`
                    : '?'}
                </Box>
              </Typography>
            </Box>
          )}
        </Box>
      ) : (
        !fullAddress && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              py: 4,
            }}
          >
            <HomeIcon sx={{ fontSize: 40, color: theme.textLight, mb: 2 }} />
            <Typography sx={{ color: theme.textLight, textAlign: 'center' }}>
              No door data available
            </Typography>
          </Box>
        )
      )}
    </Box>
  );
};

export default DealDoorData;
